import React, { useState, useEffect, useRef, useContext } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import {
  MainContainer,
  StyledCard,
  FormGrid,
  StyledFormGroup,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  GraduationGrid,
  GraduationButton,
  GraduationSection,
  GraduationLabel,
  CompositionTableWrapper,
  CompositionTable,
  CompositionTableHeader,
  CompositionTableRow,
  TableCell,
  TableSelect,
  TableInput,
  DeleteButton,
  AddItemButton,
  ImportButton,
  TagsContainer,
  Tag,
  RemoveTagButton,
  SelectContainer,
} from './style'
import CurrencyInput from '../../../../components/CurrencyInput'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import * as CollectionStyles from '../../Collections/RegisterCollection/style'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import cross from '../../../../assets/addStockModal/cross.svg'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`
const STEEL_PRODUCTS_API_URL = `${GATEWAY_URL}/product-final/product`

interface Collection {
  id: number
  name: string
}

interface Family {
  id: number
  name: string
  collectionId: number
}

interface Group {
  id: number
  name: string
}

interface GradeType {
  id: number
  name: string
}

interface Grade {
  id: number
  name: string
  typeId: number
  type: GradeType
  GraduationType?: GradeType
}

interface Stock {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
}

interface ResourceCategory {
  id: number
  name: string
}

interface Resource {
  id: number
  name: string
  description: string
  categoryId: number
  materialId?: number | null
  prices?: ResourcePrice[]
}

interface ResourcePrice {
  id: number
  price: number
  unitOfMeasurement?: {
    id: number
    name: string
    abbreviation: string
  }
}

interface MaterialVariation {
  id: number
  colorId: number | null
  stockId: number | null
  resourceId: number | null
  color: { id: number; name: string } | null
  stock: Stock | null
  resource: Resource | null
}

interface ColorVariationMapping {
  mainVariationId: number
  mainColorName: string
  itemVariationId: number | null
}

interface CompositionItem {
  id: number
  tipo: 'materia-prima' | 'insumo' | ''
  materialId?: number | null
  stockId: number | null
  resourceId: number | null
  categoryId: number | null
  descricao: string
  unidadeMedida: string
  unitOfMeasurementId?: number | null
  localEstoque: string
  consumo: string
  availableVariations: MaterialVariation[]
  autoCreated?: boolean
  productItemColors?: Array<{
    id?: number
    collectionColorId: number
    itemColorId?: number | null
    stockColorId?: number | null
    resourceId?: number | null
  }>
}

interface ProductItemColorEntry {
  id?: number
  collectionColorId: number
  itemColorId?: number | null
  stockColorId?: number | null
  materialId?: number | null
  stockId?: number | null
  resourceId?: number | null
}

interface ProductData {
  id?: number
  productCode: string
  name: string
  collectionId: number
  familyId: number
  groupId: number
  gradeInProduct?: Array<{
    id: number
    productId: number
    gradeId: number
    grade: Grade
  }>
  productItemColors?: ProductItemColorEntry[]
  composition?: {
    id: number
    fashionProductId: number
    variations: Array<{
      id: number
      compositionId: number
      stockId: number | null
      resourceId: number | null
      description: string
      stockLocation: string
      usage: string
      stock: Stock | null
      resource: Resource | null
      colorMappings: Array<{
        id: number
        fashionProductVariationId: number
        mainMaterialVariationId: number
        itemVariationId: number
        mainMaterialVariation?: MaterialVariation
        itemVariation?: MaterialVariation
      }>
    }>
  }
  compositionItems?: {
    stockId: number | null
    resourceId: number | null
    description: string
    stockLocation: string
    usage: number
    colorMappings: Array<{
      mainMaterialVariationId: number
      itemVariationId: number
    }>
  }[]

  Script?: Array<{
    id: number
    name: string
    productId: number
    ScriptMaterial?: Array<{
      id: number
      scriptId: number
      materialId: number
      idealMeasurements: number | null
      weight: number | null
      step: number
      Material: {
        id: number
        name: string
        density: number
        tenantId: number
        Stock: Array<{
          id: number
          description: string
          costPerKg?: number
          costPerMm?: number
          StockLocation?: {
            id: number
            name: string
            tenantId: number
          }
          StockColors: Array<{
            id: number
            stockId: number
            colorId: number
            ItemColor: {
              id: number
              name: string
              tenantId: number
            }
          }>
        }>
      }
    }>
    ScriptResource?: Array<{
      id: number
      scriptId: number
      resourceId: number
      weight: number | null
      step: number
      unitOfMeasurementId: number | null
      Resource: Resource & {
        Category_Resource?: {
          id: number
          name: string
          tenantId: number
        }
        ResourcePrice: ResourcePrice[]
      }
      UnitOfMeasurement?: {
        id: number
        name: string
        abbreviation: string
      }
    }>
    ScriptStage?: Array<any> // Pode ser necessário definir mais detalhadamente se necessário
  }>
  Collection?: {
    id: number
    name: string
    tenantId: number
  }
  Family?: {
    id: number
    name: string
    collectionId: number
    tenantId: number
    Collection?: {
      id: number
      name: string
      tenantId: number
    }
  }
  Group?: {
    id: number
    name: string
    tenantId: number
  }
}

interface FamilyConjunto {
  familyMaterialId: number
  collectionMaterialId: number
  materialId: number
  materialName: string
  linkedResourceMaterials: Array<{
    materialId: number
    materialName: string
    categoryId?: number | null
    categoryName?: string | null
  }>
}

interface StockColor {
  id: number
  description: string
  costPerKg?: number
  costPerMm?: number
  StockColors?: Array<{
    id: number
    stockId: number
    colorId: number
    ItemColor: {
      id: number
      name: string
      tenantId: number | null
    }
  }>
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const DownloadIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4.66675 6.66669L8.00008 10L11.3334 6.66669'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M8 10V2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const RegisterComposition: React.FC = () => {
  const navigate = useNavigate()
  const { productId } = useParams<{ productId?: string }>()
  const isEditMode = !!productId

  const { setFeedback } = useContext(FeedbackContext)

  const initializationCompletedRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const initialEditFamilyIdRef = useRef<number | null>(null)

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingData, setLoadingData] = useState<boolean>(true)
  const [isInitializing, setIsInitializing] = useState<boolean>(false)

  const [formData, setFormData] = useState({
    codigo: '',
    nomeProduto: '',
    collectionId: 0,
    familyId: 0,
    groupId: 0,
    price: 0,
  })

  const [selectedConjuntoId, setSelectedConjuntoId] = useState<number | null>(null)
  const [selectedStockColorIds, setSelectedStockColorIds] = useState<number[]>([])

  const [selectedGradeType, setSelectedGradeType] = useState<number | null>(null)

  const [allCollections, setAllCollections] = useState<Collection[]>([])
  const [allFamilies, setAllFamilies] = useState<Family[]>([])
  const [filteredFamilies, setFilteredFamilies] = useState<Family[]>([])
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([])

  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [gradeTypes, setGradeTypes] = useState<GradeType[]>([])
  const [availableGradeIds, setAvailableGradeIds] = useState<number[]>([])
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])

  const [allResourceCategories, setAllResourceCategories] = useState<ResourceCategory[]>([])
  const [resourcesByCategory, setResourcesByCategory] = useState<Record<number, Resource[]>>({})
  const [familyConjuntos, setFamilyConjuntos] = useState<FamilyConjunto[]>([])
  const [conjuntoStocks, setConjuntoStocks] = useState<StockColor[]>([])

  const [compositionItems, setCompositionItems] = useState<CompositionItem[]>([])
  const selectedInsumoMaterialIds = React.useMemo(() => {
    return new Set(
      compositionItems
        .filter(ci => ci.tipo === 'insumo' && ci.materialId != null)
        .map(ci => ci.materialId as number)
    )
  }, [compositionItems])

  const [rcDebugLogs, setRcDebugLogs] = useState<string[]>([])
  const rcDebug = (msg: string) => {
    console.log(msg)
    setRcDebugLogs(prev => [...prev, msg])
  }
  const [units, setUnits] = useState<Array<{ id: number; name: string }>>([])

  const [productData, setProductData] = useState<ProductData | null>(null)

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const resp = await axios.get(`${GATEWAY_URL}/resources/resource/util/units`, { headers })
        console.debug('[RegisterComposition] unidade fetch response', resp.data)
        if (Array.isArray(resp.data)) {
          const excluded = ['Celsius', 'Faren', 'HB', 'HRB', 'HRC', 'J', 'MPa', 'Newton', 'Tn'];
          const filtered = resp.data.filter((u: any) => {
            const name = String(u.name || '').trim();
            return !excluded.some(e => e.toLowerCase() === name.toLowerCase());
          });
          setUnits(filtered)
          console.debug('[RegisterComposition] unidades gravadas', filtered)
        }
      } catch (err) {
        console.error('Erro ao buscar unidades de medida:', err)
      }
    }
    fetchUnits()
  }, [])
  const [allCollectionColors, setAllCollectionColors] = useState<{ id: number; name: string; hex?: string }[]>([])
  const [allItemColors, setAllItemColors] = useState<{ id: number; name: string }[]>([])
  const [allVariations, setAllVariations] = useState<any[]>([])

  const [selectedCollectionColors, setSelectedCollectionColors] = useState<{ id: number; name: string; hex?: string }[]>([])
  const selectedCollectionColorsPrevLength = React.useRef<number>(0)
  const removeCollectionColor = (id: number) => {
    setSelectedCollectionColors(prev => prev.filter(c => Number(c.id) !== Number(id)))
  }

  const selectedConjunto = React.useMemo(() => {
    return familyConjuntos.find(c => c.familyMaterialId === selectedConjuntoId) || null
  }, [familyConjuntos, selectedConjuntoId])

  const insumoMaterialGroups = React.useMemo(() => {
    const source = selectedConjuntoId
      ? familyConjuntos.filter(c => c.familyMaterialId === selectedConjuntoId)
      : familyConjuntos

    const seen = new Set<string>()
    const result: Array<{ materialId: number; materialName: string; categoryId?: number | null; categoryName?: string | null }> = []

    for (const conjunto of source) {
      const linked = Array.isArray(conjunto.linkedResourceMaterials) ? conjunto.linkedResourceMaterials : []
      for (const lr of linked) {
        if (!lr?.materialId) continue
        const key = `${lr.materialId}:${lr.categoryId ?? ''}`
        if (seen.has(key)) continue
        seen.add(key)
        const label = lr.categoryName ? `${lr.materialName} (${lr.categoryName})` : lr.materialName
        result.push({ materialId: lr.materialId, materialName: label, categoryId: lr.categoryId ?? null, categoryName: lr.categoryName ?? null })
      }
    }

    return result
  }, [familyConjuntos, selectedConjuntoId])

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const res = await axios.get(`${FASHION_API_URL}/collections`, { headers })
        const data = Array.isArray(res.data) ? res.data : []
        setAllCollections(data.map((c: any) => ({ id: c.id, name: c.name })))
      } catch (error) {
        console.error('Erro ao carregar collections:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/families`, { headers })
        const data = Array.isArray(res.data) ? res.data : []
        setAllFamilies(data.map((f: any) => ({ id: f.id, name: f.name, collectionId: f.collectionId })))
      } catch (error) {
        console.error('Erro ao carregar families:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/grades`, { headers })
        const grades: Grade[] = Array.isArray(res.data) ? res.data : []

        const typesMap = new Map<number, GradeType>()
        grades.forEach((grade) => {
          const gradeType = grade.GraduationType || grade.type;
          if (gradeType && !typesMap.has(gradeType.id)) {
            typesMap.set(gradeType.id, gradeType)
          }
        })
        setGradeTypes(Array.from(typesMap.values()))

        const byType: Record<string, Grade[]> = {}
        grades.forEach((grade) => {
          const typeName = (grade.GraduationType || grade.type)?.name || 'Tipo Desconhecido'
          if (!byType[typeName]) {
            byType[typeName] = []
          }
          byType[typeName].push(grade)
        })

        Object.keys(byType).forEach((typeName) => {
          byType[typeName].sort((a, b) => {
            const aNum = parseInt(a.name)
            const bNum = parseInt(b.name)
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
            return a.name.localeCompare(b.name)
          })
        })

        setGradesByType(byType)
      } catch (error) {
        console.error('Erro ao carregar grades:', error)
      }


      try {
        const res = await axios.get(`${FASHION_API_URL}/resource-categories`, { headers })
        const categories: ResourceCategory[] = Array.isArray(res.data) ? res.data : []
        setAllResourceCategories(categories)
      } catch (error) {
        console.error('Erro ao carregar resource categories:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/resources`, { headers })
        const resources: Resource[] = Array.isArray(res.data) ? res.data : []

        const byCategory: Record<number, Resource[]> = {}
        resources.forEach((resource) => {
          if (resource.categoryId) {
            if (!byCategory[resource.categoryId]) {
              byCategory[resource.categoryId] = []
            }
            byCategory[resource.categoryId].push(resource)
          }
        })
        setResourcesByCategory(byCategory)
      } catch (error) {
        console.error('Erro ao carregar resources:', error)
      }

      try {
        const res = await axios.get(`${FASHION_API_URL}/colors`, { headers })
        const colors = Array.isArray(res.data) ? res.data : []
        setAllItemColors(colors.map((c: any) => ({ id: c.id, name: c.name })))
      } catch (error) {
        console.error('Erro ao carregar cores globais:', error)
      }

      setLoadingData(false)
    }

    fetchInitialData()
  }, [])


  useEffect(() => {

    return () => { }
  }, [formData.collectionId])

  useEffect(() => {
    if (!isEditMode || !productId) return

    const loadProduct = async () => {
      setIsInitializing(true)

      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }

        const res = await axios.get(`${FASHION_API_URL}/${productId}`, { headers })
        const product: ProductData = res.data

        rcDebug('=== PRODUTO CARREGADO ===')
        rcDebug('Product completo: ' + JSON.stringify(product, null, 2))
        rcDebug('Composition (back-end): ' + JSON.stringify(product.composition, null, 2))
        rcDebug('ProductItemColor entries (lowercase): ' + JSON.stringify(product.productItemColors || [], null, 2))
        rcDebug('ProductItemColor entries (uppercase): ' + JSON.stringify((product as any).ProductItemColor || [], null, 2))

        setProductData(product)

        setFormData({
          codigo: product.productCode || '',
          nomeProduto: product.name || '',
          collectionId: product.collectionId || 0,
          familyId: product.familyId || 0,
          groupId: product.groupId || 0,
          price: (product as any).price || 0,
        });


        if (isEditMode) {
          initialEditFamilyIdRef.current = product.familyId || null
        }


        if (isEditMode && product.familyId) {
          const loadFamilyGrades = async () => {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
              const res = await axios.get(
                `${FASHION_API_URL}/families/${product.familyId}/grades`,
                { headers }
              );
              const gradesInFamily = Array.isArray(res.data) ? res.data : [];
              const gradeIds = gradesInFamily.map((g: any) => g.gradeId);
              console.log('DEBUG GRADES - FORÇADO - Grades da família carregadas:', gradesInFamily);
              console.log('DEBUG GRADES - FORÇADO - gradeIds:', gradeIds);
              setAvailableGradeIds(gradeIds);


              setTimeout(() => {
                const gradeInProduct = productData?.gradeInProduct || (productData as any)?.GradeInProduct || [];

                if (gradeInProduct?.length) {
                  const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
                    const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
                    return gradeId;
                  }).filter(Boolean);

                  const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
                    const isIncluded = gradeIds.some((g: any) => {
                      const id = typeof g === 'object' ? g.id : g;
                      return id === gradeId;
                    });
                    return isIncluded;
                  });

                  if (validGradeIds.length > 0) {
                    const validGradeObjects = validGradeIds.map((gradeId: number) => {
                      const gradeObj = gradeIds.find((g: any) => {
                        const id = typeof g === 'object' ? g.id : g;
                        return id === gradeId;
                      });
                      return gradeObj || gradeId;
                    });

                    setSelectedGradeIds(validGradeObjects);

                    if (validGradeObjects.length > 0) {
                      const firstGrade = validGradeObjects[0];
                      if (typeof firstGrade === 'object' && firstGrade !== null) {
                        const gradeAsAny = firstGrade as any;
                        const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
                        if (gradeType) {
                          setSelectedGradeType(gradeType);
                        }
                      }
                    }
                  }
                }
              }, 100);
            } catch (error) {
              console.log('DEBUG GRADES - FORÇADO - Erro ao carregar grades da família:', error);
            }
          };

          loadFamilyGrades();
        }


        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            groupId: product.groupId || 0
          }));


          if (product.groupId) {
            setFilteredGroups(prev => {
              if (!prev.some(g => g.id === product.groupId)) {

                return [...prev, {
                  id: product.groupId,
                  name: product.Group?.name || `Grupo ${product.groupId}`
                }];
              }
              return prev;
            });
          }

          console.log('DEBUG FORM DATA ATUALIZADO:', {
            groupId: product.groupId,
            groupName: product.Group?.name,
            filteredGroups: filteredGroups,
            filteredGroupsLength: filteredGroups.length
          });
        }, 100);


        console.log('DEBUG GRADES - gradeIds do produto:', product.gradeInProduct?.map(g => g.gradeId) || []);
        console.log('DEBUG GRADES - availableGradeIds:', availableGradeIds);
        console.log('DEBUG GRADES - product.gradeInProduct:', product.gradeInProduct);

        if (product.gradeInProduct?.length || (product as any).GradeInProduct?.length) {


          const gradeInProduct = product.gradeInProduct || (product as any).GradeInProduct || [];
          if (gradeInProduct.length > 0) {
            const firstGradeData = gradeInProduct[0];

            const firstGrade = firstGradeData.grade || firstGradeData.Grade;
            const typeId = firstGrade?.GraduationType?.id || firstGrade?.type?.id || firstGradeData.grade?.GraduationType?.id;
            if (typeId) setSelectedGradeType(typeId);
          }
        }


        let itemIdCounter = 1;
        if (product.Script?.length) {
          const items = product.Script.flatMap((script: any) => {
            const scriptItems: any[] = [];


            if (script.ScriptMaterial?.length) {
              script.ScriptMaterial.forEach((sm: any) => {
                if (sm.Material?.id) {
                  const material = sm.Material;
                  const stock = material.Stock?.[0]; // Pegando o primeiro stock disponível

                  scriptItems.push({
                    id: itemIdCounter++, // Usar contador global para garantir IDs únicos
                    tipo: 'materia-prima' as const,
                    materialId: material.id,
                    stockId: stock?.id || null,
                    resourceId: null,
                    categoryId: null,
                    descricao: '',
                    unidadeMedida: sm.UnitOfMeasurement?.name || '',
                    unitOfMeasurementId: sm.unitOfMeasurementId || null,
                    localEstoque: stock?.StockLocation?.name || '',
                    consumo: sm.weight?.toString() || sm.idealMeasurements?.toString() || '',
                    availableVariations: [],
                    colorMappings: [],
                  });
                }
              });
            }


            if (script.ScriptResource?.length) {
              script.ScriptResource.forEach((sr: any) => {
                if (sr.Resource?.id) {
                  const resource = sr.Resource;


                  let derivedMaterialId: number | null = resource.materialId ?? null;
                  let derivedCategoryId: number | null = resource.categoryId ?? null;
                  if (!derivedMaterialId && resourcesByCategory) {
                    for (const [catId, resources] of Object.entries(resourcesByCategory)) {
                      const found = (resources || []).find((r) => r.id === resource.id);
                      if (found) {
                        derivedMaterialId = found.materialId ?? derivedMaterialId;
                        derivedCategoryId = Number(catId);
                        break;
                      }
                    }
                  }

                  scriptItems.push({
                    id: itemIdCounter++, // Usar contador global para garantir IDs únicos
                    tipo: 'insumo' as const,
                    materialId: derivedMaterialId,
                    stockId: null,
                    resourceId: resource.id,
                    categoryId: derivedCategoryId,
                    descricao: resource.description || resource.name || '',
                    unidadeMedida: sr.UnitOfMeasurement?.name || '',
                    unitOfMeasurementId: sr.unitOfMeasurementId || null,
                    localEstoque: '',
                    consumo: sr.weight?.toString() || '',
                    availableVariations: [],
                    colorMappings: [],
                  });
                }
              });
            }

            return scriptItems;
          });

          console.log('Items de composição criados a partir do Script:', items);

          const serverColors: any[] =
            (product as any).productItemColors ||
            (product as any).ProductItemColor ||
            [];
          rcDebug('[RC] serverColors do produto: ' + JSON.stringify(serverColors, null, 2));
          console.debug('[RC] serverColors with StockColors info', serverColors.map(sc => ({
            id: sc.id,
            stockColorId: sc.stockColorId,
            stockInfo: sc.StockColors ? { stockId: sc.StockColors.stockId, materialId: sc.StockColors.Stock?.materialId } : null
          })));

          setAllItemColors(prev => {
            const extras: { id: number; name: string }[] = [];
            serverColors.forEach((sc: any) => {

              let name: string | null = null;
              if (sc.ItemColor?.name) name = sc.ItemColor.name;
              else if (sc.Resource?.ItemColor?.name) name = sc.Resource.ItemColor.name;
              else if (sc.StockColors?.ItemColor?.name) name = sc.StockColors.ItemColor.name;

              const id = sc.itemColorId ?? sc.stockColorId;
              if (name && id != null && !prev.some(c => c.id === id) && !extras.some(c => c.id === id)) {
                extras.push({ id, name });
              }
            });
            return extras.length ? [...prev, ...extras] : prev;
          });
          console.debug('[RC] composition items before mapping colors', items);
          let itemsWithColors = items.map((item) => {

            const related = serverColors.filter((pc) => {

              const scStockId = pc.StockColors?.stockId;
              const scMaterialId = pc.StockColors?.Stock?.materialId;


                if (item.stockId && scStockId === item.stockId) {
                  return true;
                }


                if (item.materialId && scMaterialId === item.materialId) {
                  return true;
                }


                if (item.resourceId) {
                  if (pc.resourceId === item.resourceId) return true;
                  if (pc.Resource?.id === item.resourceId) return true;
                }


                if (item.tipo !== 'materia-prima' && item.materialId && pc.Resource?.materialId === item.materialId) {
                  return true;
                }


            });

            const mapped = related.map((pc: any) => {

              let resolvedItemColorId = pc.itemColorId;
              if (resolvedItemColorId == null && pc.ItemColor?.id) {
                resolvedItemColorId = pc.ItemColor.id;
              }
              if (resolvedItemColorId == null && pc.Resource?.itemColorId) {
                resolvedItemColorId = pc.Resource.itemColorId;
              }

              if (resolvedItemColorId == null && pc.StockColors?.colorId) {
                resolvedItemColorId = pc.StockColors.colorId;
              }
              if (resolvedItemColorId == null && pc.stockColorId) {
                console.debug('[RC] no itemColorId for pc, stockColorId', pc.stockColorId);
              }
              return {
                id: pc.id,
                collectionColorId: pc.collectionColorId,
                itemColorId: resolvedItemColorId,
                stockColorId: pc.stockColorId,
                materialId: pc.materialId,
                resourceId: pc.resourceId || (pc.Resource ? pc.Resource.id : null),
                stockId: pc.stockId || pc.StockColors?.stockId,
              }
            });



            const newItem = { ...item, productItemColors: mapped.length ? mapped : item.productItemColors } as CompositionItem & { stockId?: number };
            if (newItem.tipo === 'materia-prima') {
              const relWithStock = related.find((pc: any) => pc.stockId || pc.StockColors?.stockId);
              if (relWithStock) {
                const sid = relWithStock.stockId || relWithStock.StockColors?.stockId;
                if (sid && sid !== newItem.stockId) {
                  newItem.stockId = sid;
                  console.debug('[RC] assigned stockId from serverColors', newItem.stockId, 'for item', item.id);
                }
              }
            }
            return newItem;
          });


          itemsWithColors = await Promise.all(itemsWithColors.map(async (it) => {
            if (it.tipo === 'materia-prima') {
              if (!it.stockId && it.materialId) {
                try {
                  const token = localStorage.getItem('token')
                  const headers = token ? { Authorization: `Bearer ${token}` } : {}
                  const res = await axios.get(`${FASHION_API_URL}/stocks`, { headers, params: { materialId: it.materialId } })
                  const stocks: any[] = Array.isArray(res.data) ? res.data : []
                  if (stocks.length > 0) {
                    it.stockId = stocks[0].id
                  }
                } catch (e) {
                  console.error('erro ao buscar stock para material (edição)', e)
                }
              }


              if (it.stockId && !it.materialId) {
                try {
                  const token = localStorage.getItem('token')
                  const headers = token ? { Authorization: `Bearer ${token}` } : {}
                  const res = await axios.get(`${FASHION_API_URL}/stocks/${it.stockId}`, { headers })
                  if (res.data && res.data.materialId) {
                    it.materialId = res.data.materialId
                  }
                } catch (e) {
                  console.error('erro ao buscar material a partir do stock (edição)', e)
                }
              }
            }
            return it
          }));

          rcDebug('Items de composição após anexar cores do servidor: ' + JSON.stringify(itemsWithColors, null, 2));
          setCompositionItems(itemsWithColors);
          rcDebug('[RC] composição inicializada (itemsWithColors): ' + JSON.stringify(itemsWithColors, null, 2));


          (async () => {
            const enriched = await Promise.all(itemsWithColors.map(async (it) => {
              let variations: any[] = [];
              if (it.tipo === 'materia-prima') {

                const stockIds = new Set<number>();
                if (it.stockId) stockIds.add(it.stockId);
                (it.productItemColors || []).forEach((pc: any) => {
                  if (pc.stockId) stockIds.add(pc.stockId);
                  else if (pc.StockColors?.stockId) stockIds.add(pc.StockColors.stockId);
                });
                if (stockIds.size) {
                  for (const sid of stockIds) {
                    const vs = await fetchStockColors(sid);
                    variations.push(...vs);
                  }
                } else if (it.materialId) {
                  variations = await fetchStockColors(undefined, it.materialId);
                }

                variations = variations.filter((v, i, a) => a.findIndex(x => x.id === v.id) === i);
              } else if (it.resourceId) {
                variations = await fetchResourceColors(it.resourceId);
              }
              const mapped = variations.map(v => ({
                id: v.id,
                colorId: v.colorId,
                stockId: it.stockId || null,
                resourceId: it.resourceId || null,
                color: { id: v.id, name: v.color.name },
                stock: null,
                resource: null,
              }));

              setAllVariations(prev => {
                const combined = [...prev];
                mapped.forEach(m => { if (!combined.some(x => x.id === m.id)) combined.push(m); });
                return combined;
              });


              const colorMappings = mapped.map(m => {
                let selectedVariation: number | null = null;
                if (it.productItemColors && Array.isArray(it.productItemColors)) {
                  const matching = it.productItemColors.find((pc: any) =>
                    (pc.stockColorId && pc.stockColorId === m.id) ||
                    (pc.itemColorId && pc.itemColorId === m.colorId)
                  );
                  if (matching) selectedVariation = m.id;
                }
                return {
                  mainVariationId: m.id,
                  mainColorName: m.color.name,
                  itemVariationId: selectedVariation,
                };
              });

              console.debug('[RC] item', it.id, 'variations', mapped, 'mappings', colorMappings);

              return {
                ...it,
                availableVariations: mapped,
                colorMappings,
              };
            }));
            setCompositionItems(enriched);
            rcDebug('[RC] composição enriquecida com variações por item: ' + JSON.stringify(enriched, null, 2));
          })();

          if (product.Script) {
            const allColorIds: number[] = [];
            const colorMap: Record<number, string> = {};

            product.Script.forEach(script => {
              if (script.ScriptMaterial) {
                script.ScriptMaterial.forEach(sm => {
                  if (sm.Material?.Stock) {
                    sm.Material.Stock.forEach(stock => {
                      if (stock.StockColors) {
                        stock.StockColors.forEach(sc => {
                          if (!allColorIds.includes(sc.id)) {
                            allColorIds.push(sc.id);
                          }

                          if (sc.ItemColor?.name) {
                            colorMap[sc.id] = sc.ItemColor.name;
                          }
                        });
                      }
                    });
                  }
                });
              }
            });


            const pics = ((product as any).ProductItemColor || []) as any[];
            pics.forEach(pic => {
              if (pic.stockColorId && typeof pic.stockColorId === 'number') {

                if (!allColorIds.includes(pic.stockColorId)) {
                  allColorIds.push(pic.stockColorId);
                }
              }
            });

            rcDebug('DEBUG CORES - Cores encontradas no Script: ' + JSON.stringify(allColorIds));
            rcDebug('DEBUG CORES - Mapa de cores: ' + JSON.stringify(colorMap));
            rcDebug('[RC] baseFabricColors candidatos: ' + JSON.stringify(allColorIds.map(id => {
              const varRec = allVariations.find(v => v.id === id);
              return {
                variationId: id,
                colorId: varRec ? varRec.colorId : id,
                colorName: colorMap[id] || `Cor ${id}`,
              };
            }), null, 2));

            if (allColorIds.length > 0) {
              setSelectedStockColorIds(allColorIds);
            }
          }
        } else {
          rcDebug('[RC] produto sem Script (composição) no retorno da API.');

          const variations = (product as any).composition?.variations || [];
          rcDebug('[RC] product.composition.variations (fallback): ' + JSON.stringify(variations, null, 2));

          if (variations.length) {
            const items = variations.map((v: any) => {
              const isMateriaPrima = Boolean(v.stockId);
              return {
                id: itemIdCounter++,
                tipo: isMateriaPrima ? 'materia-prima' as const : 'insumo' as const,
                materialId: v.stock?.materialId ?? null,
                stockId: v.stockId ?? null,
                resourceId: v.resourceId ?? null,
                categoryId: null,
                descricao: v.description || '',
                unidadeMedida: '',
                unitOfMeasurementId: null,
                localEstoque: v.stockLocation || '',
                consumo: v.usage != null ? String(v.usage) : '',
                availableVariations: [],
                colorMappings: [],
              };
            });
            console.debug('[RC] Items de composição criados a partir de product.composition:', items);
            setCompositionItems(items);
          } else {
            rcDebug('[RC] product.composition também vazio - nenhuma composição carregada.');
          }
        }



        setTimeout(() => {
          setFormData(prev => ({
            ...prev,
            groupId: product.groupId || 0
          }));
        }, 100);


        setIsInitializing(false)
        initializationCompletedRef.current = true
      } catch (error) {
        console.error('Erro ao carregar produto:', error)
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro ao carregar dados do produto.',
        })
        setIsInitializing(false)
      }
    }

    loadProduct();

    return () => {
      isInitialLoadRef.current = true
      initializationCompletedRef.current = false
    }
  }, [isEditMode, productId]);


  useEffect(() => {
    if (!isEditMode || !productData) return
    if (!allCollectionColors || allCollectionColors.length === 0) return

    try {
      const productColors = (productData as any).ProductItemColor || []
      const collectionColorIds: number[] = productColors
        .map((pc: any) => pc.collectionColorId)
        .filter((id: any) => id !== null && id !== undefined)
      const uniqueIds = Array.from(new Set(collectionColorIds))

      const selected = uniqueIds.map(id => {
        const found = allCollectionColors.find(c => Number(c.id) === Number(id));
        if (found) return found;

        const pic = (productData as any)?.ProductItemColor?.find((p: any) => p.collectionColorId === id);
        const name = pic?.ItemColor?.name || pic?.ResourceColor?.ItemColor?.name || pic?.stockColorId &&
          (pic.ItemColor?.name || `Cor ${pic.stockColorId}`) || `Cor ${id}`;
        return { id, name };
      });

      if (selected.length > 0) {
        setSelectedCollectionColors(selected);
      }
    } catch (err) {
      console.error('Erro ao popular selectedCollectionColors no modo edição:', err)
    }
  }, [isEditMode, productData, allCollectionColors])


  useEffect(() => {
    if (!isEditMode || !isInitializing || !familyConjuntos.length || !compositionItems.length) return;

    console.log('Verificando seleção de conjunto correspondente...');
    console.log('familyConjuntos:', familyConjuntos);
    console.log('compositionItems:', compositionItems);

    let matched = false;
    const firstMaterialItem = compositionItems.find(item => item.tipo === 'materia-prima' && (item.materialId || item.stockId));
    if (firstMaterialItem) {
      console.log('Item de matéria-prima encontrado:', firstMaterialItem);

      const materialKey = firstMaterialItem.materialId || firstMaterialItem.stockId;
      const matchingConjunto = familyConjuntos.find(c => c.materialId === materialKey);
      if (matchingConjunto) {
        console.log('Conjunto correspondente encontrado e selecionado:', matchingConjunto);
        setSelectedConjuntoId(matchingConjunto.familyMaterialId);
        setIsInitializing(false);
        matched = true;
        initializationCompletedRef.current = true;
      } else {
        console.log('Nenhum conjunto correspondente encontrado para o materialId:', materialKey);
      }
    }

    if (!matched) {
      setIsInitializing(false);
      initializationCompletedRef.current = true;
    }
  }, [isEditMode, isInitializing, familyConjuntos, compositionItems]);






  useEffect(() => {
    if (!isEditMode) return;

    if (selectedGradeIds.length > 0 && availableGradeIds.length > 0) {
      selectedGradeIds.forEach(gradeId => {

        const gradeDetail = Array.isArray(availableGradeIds) && typeof availableGradeIds[0] === 'object'
          ? availableGradeIds.find((g: any) => g.id === gradeId)
          : null;

        if (gradeDetail && typeof gradeDetail !== 'number') {
          console.log(`DEBUG GRADES - Informações completas da grade ${gradeId}:`, {
            id: (gradeDetail as any).id,
            name: (gradeDetail as any).name,
            typeId: (gradeDetail as any).typeId,
            typeName: (gradeDetail as any).GraduationType?.name || (gradeDetail as any).type?.name || 'Tipo não encontrado'
          });
        } else {

          if (Array.isArray(availableGradeIds) && typeof availableGradeIds[0] === 'number') {
            console.log(`DEBUG GRADES - Grade ${gradeId} está na lista de IDs disponíveis`);
          } else {
            console.log(`DEBUG GRADES - Grade ${gradeId} não encontrada em availableGradeIds`);
          }
        }


        try {
          const productItemColors = (productData as any)?.ProductItemColor || []
          console.debug('[RC] productItemColors do servidor', productItemColors)
          if (productItemColors.length && compositionItems.length) {
            const updated = compositionItems.map((it: any) => ({ ...it, productItemColors: it.productItemColors ? [...it.productItemColors] : [] }))
            productItemColors.forEach((pic: any) => {
              let derivedItemColorId: number | null = null
              if (pic.itemColorId) derivedItemColorId = pic.itemColorId
              else if (pic.ItemColor?.id) derivedItemColorId = pic.ItemColor.id
              else if (pic.Resource?.itemColorId) derivedItemColorId = pic.Resource.itemColorId

              const match = updated.find((u: any) => {

                if (pic.resourceId && u.resourceId === pic.resourceId) return true
                if (pic.Resource?.id && u.resourceId === pic.Resource.id) return true


                if (u.materialId && pic.Resource?.materialId && u.materialId === pic.Resource.materialId) return true
                if (u.materialId && pic.materialId && u.materialId === pic.materialId) return true


                if (u.stockId && pic.stockId && u.stockId === pic.stockId) return true
                if (u.stockId && pic.StockColors?.stockId && u.stockId === pic.StockColors.stockId) return true

                return false
              })

              if (match) {
                const prevArr = match.productItemColors || []
                prevArr.push({
                  id: pic.id,
                  collectionColorId: pic.collectionColorId,
                  itemColorId: derivedItemColorId,
                  stockColorId: pic.stockColorId || null,
                  resourceId: pic.resourceId || (pic.Resource ? pic.Resource.id : null),
                })
                match.productItemColors = prevArr
              }
            })

            setCompositionItems(updated)
            console.debug('[RC] compositionItems após mapear colors', updated)
          }
        } catch (mapErr) {
          console.error('Erro ao mapear ProductItemColor para compositionItems:', mapErr)
        }
      });
    }
  }, [isEditMode, selectedGradeIds, availableGradeIds]);






  useEffect(() => {
    if (!formData.familyId || formData.familyId === 0) return

    const loadFamilyData = async () => {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const res = await axios.get(
          `${FASHION_API_URL}/families/${formData.familyId}/grades`,
          { headers }
        )
        const gradesInFamily = Array.isArray(res.data) ? res.data : []
        const gradeIds = gradesInFamily.map((g: any) => g.gradeId)
        console.log('DEBUG GRADES - Grades da família carregadas:', gradesInFamily);
        console.log('DEBUG GRADES - gradeIds:', gradeIds);
        setAvailableGradeIds(gradeIds)



        console.log('DEBUG GRADES - Carregando grades da família para edição, mas seleção será feita posteriormente');
      } catch (error) {
        console.log('DEBUG GRADES - Erro ao carregar grades da família:', error);
        setAvailableGradeIds([])
      }

      try {
        const res = await axios.get(
          `${FASHION_API_URL}/families/${formData.familyId}/materials`,
          { headers }
        )
        const materials = Array.isArray(res.data) ? res.data : []

        console.log('[INSUMO DEBUG] === Raw API /families/materials ===')
        materials.forEach((m: any) => {
          console.log(`[INSUMO DEBUG] FamilyMaterial id=${m.id} collectionMaterialId=${m.collectionMaterialId}`)
          console.log(`  CollectionMaterial.Material:`, m.CollectionMaterial?.Material?.name)
        })

        const conjuntos: FamilyConjunto[] = materials.map((m: any) => ({
          familyMaterialId: m.id,
          collectionMaterialId: m.collectionMaterialId,
          materialId: m.CollectionMaterial?.materialId || m.CollectionMaterial?.Material?.id || null,
          materialName: m.CollectionMaterial?.Material?.name || 'Material',
          linkedResourceMaterials: Array.isArray(m.CollectionMaterial?.CollectionMaterialResources)
            ? m.CollectionMaterial.CollectionMaterialResources
              .map((cmr: any) => ({
                materialId: cmr?.materialId,
                materialName: cmr?.Material?.name || '',
                categoryId: cmr?.categoryResourceId ?? cmr?.Category_Resource?.id ?? null,
                categoryName: cmr?.Category_Resource?.name || null,
              }))
              .filter((v: any) => typeof v?.materialId === 'number' && String(v?.materialName || '').trim().length > 0)
            : [],
        }))

        console.log('Conjuntos carregados da família:', conjuntos);
        setFamilyConjuntos(conjuntos)




        const shouldResetForEdit = isEditMode && initializationCompletedRef.current && formData.familyId !== initialEditFamilyIdRef.current

        if (!isEditMode || shouldResetForEdit) {
          setSelectedConjuntoId(null)
          setSelectedStockColorIds([])
          setConjuntoStocks([])
          setCompositionItems([])
        }

        if (isEditMode && productData && conjuntos.length > 0 && !initializationCompletedRef.current) {
          if (productData.Script) {
            for (const script of productData.Script) {
              if (script.ScriptMaterial) {
                for (const sm of script.ScriptMaterial) {
                  if (sm.Material?.Stock) {
                    for (const stock of sm.Material.Stock) {

                      const matchingConjunto = conjuntos.find(c =>
                        c.materialId === sm.Material?.id
                      );
                      if (matchingConjunto) {
                        console.log('Conjunto correspondente encontrado para edição:', matchingConjunto);
                        setSelectedConjuntoId(matchingConjunto.familyMaterialId);
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar materiais da família:', error);
        setFamilyConjuntos([])
      }
    }

    loadFamilyData()
  }, [formData.familyId, isEditMode, productData?.gradeInProduct, productData?.Script])

  useEffect(() => {
    if (!selectedConjuntoId) {
      setConjuntoStocks([])
      return
    }

    const conjunto = familyConjuntos.find(c => c.familyMaterialId === selectedConjuntoId)
    if (!conjunto?.materialId) {
      console.log('Conjunto não encontrado ou sem materialId')
      setConjuntoStocks([])
      return
    }

    const loadGroupsFromCollection = async () => {
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` };
        const familyIdToUse = productData?.familyId || formData.familyId
        if (!familyIdToUse) return

        try {
          const famRes = await axios.get(`${FASHION_API_URL}/families/${familyIdToUse}`, { headers })
          const familyData = famRes.data
          const familyGroups = Array.isArray(familyData?.GroupInFamily)
            ? familyData.GroupInFamily.map((gf: any) => gf.Group || gf.group).filter((g: any) => g && g.id)
            : []
          console.log('DEBUG GRUPOS - Grupos da família (conjunto):', familyGroups)
          setFilteredGroups(familyGroups)
        } catch (famErr) {
          console.error('DEBUG GRUPOS - Erro ao buscar grupos da família (conjunto):', famErr)
        }
      } catch (error) {
        console.error('Erro ao carregar grupos (edição):', error)
        setFilteredGroups([])
      }
    }

    loadGroupsFromCollection()
  }, [selectedConjuntoId, familyConjuntos])


  useEffect(() => {
    if (!selectedConjunto || !compositionItems.length) return

    setCompositionItems((prev) =>
      prev.map((item) => {
        if (item.tipo !== 'insumo') return item
        if (item.materialId !== selectedConjunto.materialId) return item
        if (item.descricao === selectedConjunto.materialName) return item
        return {
          ...item,
          descricao: selectedConjunto.materialName,
        }
      })
    )
  }, [selectedConjunto, compositionItems.length])


  useEffect(() => {
    if (!familyConjuntos.length) return
    setCompositionItems(prev => prev.map(item => {
      if (item.tipo !== 'insumo' || item.materialId) return item
      return item
    }))
  }, [familyConjuntos])



  useEffect(() => {
    if (!isEditMode || !productData) {
      return;
    }


    if (!availableGradeIds || availableGradeIds.length === 0) {

      const timer = setTimeout(() => {
        if (productData && availableGradeIds && availableGradeIds.length > 0) {

          const gradeInProduct = productData.gradeInProduct || (productData as any).GradeInProduct || [];

          if (gradeInProduct?.length) {
            const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
              const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
              return gradeId;
            }).filter(Boolean);

            const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
              const isIncluded = availableGradeIds.some((g: any) => {
                const id = typeof g === 'object' ? g.id : g;
                return id === gradeId;
              });
              return isIncluded;
            });

            if (validGradeIds.length > 0) {
              const validGradeObjects = validGradeIds.map((gradeId: number) => {
                const gradeObj = availableGradeIds.find((g: any) => {
                  const id = typeof g === 'object' ? g.id : g;
                  return id === gradeId;
                });
                return gradeObj || gradeId;
              });

              setSelectedGradeIds(validGradeObjects);

              if (validGradeObjects.length > 0) {
                const firstGrade = validGradeObjects[0];
                if (typeof firstGrade === 'object' && firstGrade !== null) {
                  const gradeAsAny = firstGrade as any;
                  const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
                  if (gradeType) {
                    setSelectedGradeType(gradeType);
                  }
                }
              }
            }
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }


    const gradeInProduct = productData.gradeInProduct || (productData as any).GradeInProduct || [];

    if (gradeInProduct?.length) {
      const gradeIdsFromProduct = gradeInProduct.map((g: any) => {
        const gradeId = g.gradeId || (g.Grade ? g.Grade.id : g.gradeId);
        return gradeId;
      }).filter(Boolean);

      const validGradeIds = gradeIdsFromProduct.filter((gradeId: number) => {
        const isIncluded = availableGradeIds.some((g: any) => {
          const id = typeof g === 'object' ? g.id : g;
          return id === gradeId;
        });
        return isIncluded;
      });

      if (validGradeIds.length > 0) {
        const validGradeObjects = validGradeIds.map((gradeId: number) => {
          const gradeObj = availableGradeIds.find((g: any) => {
            const id = typeof g === 'object' ? g.id : g;
            return id === gradeId;
          });
          return gradeObj || gradeId;
        });

        setSelectedGradeIds(validGradeObjects);

        if (validGradeObjects.length > 0) {
          const firstGrade = validGradeObjects[0];
          if (typeof firstGrade === 'object' && firstGrade !== null) {
            const gradeAsAny = firstGrade as any;
            const gradeType = gradeAsAny.GraduationType?.id || gradeAsAny.typeId;
            if (gradeType) {
              setSelectedGradeType(gradeType);
            }
          }
        }
      }
    }
  }, [isEditMode, productData, availableGradeIds]);

  useEffect(() => {


    if (isEditMode && !initializationCompletedRef.current) return

    if (formData.collectionId > 0) {
      const filtered = allFamilies.filter((f) => f.collectionId === formData.collectionId)
      setFilteredFamilies(filtered)

      setFilteredGroups([])
    } else {
      setFilteredFamilies([])
      console.debug('Clearing filteredGroups due to collection change')
      setFilteredGroups([])
      setFormData((prev) => ({ ...prev, familyId: 0, groupId: 0 }))
      setSelectedGradeIds([])
      setSelectedGradeType(null)
      setAvailableGradeIds([])
      setSelectedConjuntoId(null)
      setSelectedStockColorIds([])
      setFamilyConjuntos([])
      setConjuntoStocks([])
      setCompositionItems([])
    }
  }, [formData.collectionId, allFamilies, isEditMode])


  useEffect(() => {
    const previousLength = selectedCollectionColorsPrevLength.current
    const currentLength = selectedCollectionColors.length


    if (currentLength > 0 && previousLength === 0) {
      const el = document.getElementById('composition-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }

    selectedCollectionColorsPrevLength.current = currentLength

    if (!currentLength) return

    setCompositionItems(prev => prev.map(item => {
      const prevArr: ProductItemColorEntry[] = item.productItemColors ? [...item.productItemColors] : []

      selectedCollectionColors.forEach(col => {
        if (!prevArr.some(p => p.collectionColorId === col.id)) {
          prevArr.push({ collectionColorId: col.id, itemColorId: null })
        }
      })

      const filtered = prevArr.filter(p => selectedCollectionColors.some(sc => sc.id === p.collectionColorId))
      return { ...item, productItemColors: filtered }
    }))
  }, [selectedCollectionColors])




  useEffect(() => {
    if (!isEditMode || !productData) return

    const familyIdForEdit = productData?.familyId;
    if (familyIdForEdit) {
      const loadFamilyGroups = async () => {
        try {
          const token = localStorage.getItem('token')
          const headers = { Authorization: `Bearer ${token}` }
          const res = await axios.get(`${FASHION_API_URL}/families/${familyIdForEdit}`, { headers })
          const familyData = res.data
          const groups = Array.isArray(familyData?.GroupInFamily)
            ? familyData.GroupInFamily.map((gf: any) => gf.Group || gf.group).filter((g: any) => g && g.id)
            : []
          console.debug('Populating filteredGroups for edit mode', { familyId: familyIdForEdit, groupsCount: groups.length })
          setFilteredGroups(groups)
        } catch (error) {
          console.error('Erro ao carregar grupos da família (edição):', error)
          console.debug('Clearing filteredGroups due to edit load error')
          setFilteredGroups([])
        }
      }

      loadFamilyGroups()
    } else {
      setFilteredGroups([])
    }
  }, [isEditMode, productData])


  useEffect(() => {
    if (!isEditMode || !formData.collectionId || filteredFamilies.length > 0) return

    const filtered = allFamilies.filter((f) => f.collectionId === formData.collectionId)
    setFilteredFamilies(filtered)
  }, [isEditMode, formData.collectionId, allFamilies, filteredFamilies.length])




  useEffect(() => {
    if (!formData.familyId || formData.familyId === 0) {
      setFilteredGroups([])
      return
    }

    const loadFamilyGroupsOnSelect = async () => {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }
      try {
        const res = await axios.get(`${FASHION_API_URL}/families/${formData.familyId}`, { headers })
        const familyData = res.data
        const groups = Array.isArray(familyData?.GroupInFamily)
          ? familyData.GroupInFamily.map((gf: any) => gf.Group || gf.group).filter((g: any) => g && g.id)
          : []
        console.debug('Populating filteredGroups due to family selection', { familyId: formData.familyId, groupsCount: groups.length })
        setFilteredGroups(groups)


        const familyColors = Array.isArray(familyData?.FamilyColor)
          ? familyData.FamilyColor.map((fc: any) => fc.CollectionColor || fc.collectionColor).filter(Boolean).map((c: any) => ({ id: c.id, name: c.name, hex: c.hex }))
          : []

        setAllCollectionColors(familyColors)


        setSelectedCollectionColors([])
      } catch (error) {
        console.debug('Clearing filteredGroups due to family selection load error')
        setFilteredGroups([])
      }
    }

    loadFamilyGroupsOnSelect()
  }, [formData.familyId])


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name === 'familyId') {
      setFormData((prev) => ({
        ...prev,
        familyId: Number(value),
        groupId: 0,
      }))
      setFilteredGroups([])
      return
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('Id') ? Number(value) : value,
    }))
  }

  const handleGradeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = Number(e.target.value)
    setSelectedGradeType(typeId || null)

    setSelectedGradeIds([])
  }

  const handleGradeToggle = (gradeId: number) => {
    if (!availableGradeIds.includes(gradeId)) return

    setSelectedGradeIds((prev) =>
      prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]
    )
  }

  useEffect(() => {
    if (selectedConjuntoId && isEditMode) {
      const mat = familyConjuntos.find(c => c.familyMaterialId === selectedConjuntoId)?.materialId || null;
      if (mat != null) {
        setCompositionItems(prev => prev.map(it => it.tipo === 'materia-prima' ? { ...it, materialId: mat } : it));
      }
    }
  }, [selectedConjuntoId, familyConjuntos, isEditMode]);

  const handleConjuntoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const conjuntoId = Number(e.target.value) || null
    setSelectedConjuntoId(conjuntoId)
    setSelectedStockColorIds([])
    setConjuntoStocks([])

    console.debug('handleConjuntoChange', { conjuntoId, currentFamilyId: formData.familyId, currentFilteredGroupsLength: filteredGroups.length })

    if (conjuntoId) {
      const conjunto = familyConjuntos.find(c => c.familyMaterialId === conjuntoId)
      console.log('[RC] escolhido conjunto', conjunto);
      if (conjunto) {
        const baseMat = conjunto.materialId || null;

        setCompositionItems(prev => {
          const first: CompositionItem = {
            id: 1,
            tipo: 'materia-prima',
            materialId: baseMat,
            stockId: null,
            resourceId: null,
            categoryId: null,
            descricao: conjunto.materialName || '',
            unidadeMedida: '',
            unitOfMeasurementId: null,
            localEstoque: '',
            consumo: '',
            availableVariations: [],
            autoCreated: true,
            productItemColors: selectedCollectionColors.map(c => ({ collectionColorId: c.id, itemColorId: null })),
          };

          const others = prev.slice(1).map(it =>
            it.tipo === 'materia-prima' ? { ...it, materialId: baseMat } : it
          );
          return [first, ...others];
        })
      }
    } else {
      setCompositionItems([])
    }
  }

  const handleStockColorSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stockId = Number(e.target.value)
    if (!stockId) return
    const stock = conjuntoStocks.find(s => s.id === stockId)
    if (!stock) return
    const scIds: number[] = (stock.StockColors || []).map((sc: any) => sc.id)
    setSelectedStockColorIds(prev => Array.from(new Set([...prev, ...scIds])))
  }

  const removeStockColor = (stockId: number) => {

    setSelectedStockColorIds(selectedStockColorIds.filter(id => id !== stockId))
  }

  const addCompositionItem = () => {
    const newId = compositionItems.length > 0 ? Math.max(...compositionItems.map((i) => i.id)) + 1 : 1
    const hasMaterialItem = compositionItems.some((i) => i.tipo === 'materia-prima')
    const isFirst = compositionItems.length === 0

    setCompositionItems([
      ...compositionItems,
      {
        id: newId,

        tipo: isFirst && !hasMaterialItem ? 'materia-prima' : 'insumo',
        stockId: null,
        resourceId: null,
        categoryId: null,
        descricao: '',
        unidadeMedida: '',
        unitOfMeasurementId: null,
        localEstoque: '',
        consumo: '',
        availableVariations: [],
        autoCreated: isFirst ? true : undefined,
        productItemColors: selectedCollectionColors.map(c => ({ collectionColorId: c.id, itemColorId: null })),
      },
    ])
  }

  const removeCompositionItem = (id: number) => {
    setCompositionItems(compositionItems.filter((item) => item.id !== id))
  }

  const handleCompositionTypeChange = (itemId: number, tipo: 'materia-prima' | 'insumo' | '') => {
    const hasOtherMaterial = compositionItems.some(
      (item) => item.id !== itemId && item.tipo === 'materia-prima',
    )

    const resolvedTipo = tipo === 'materia-prima' && hasOtherMaterial ? 'insumo' : tipo

    if (tipo === 'materia-prima' && resolvedTipo === 'insumo') {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Já existe um item do tipo material. Novos itens devem ser do tipo insumo (resource).',
      })
    }

    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            tipo: resolvedTipo,
            materialId: null,
            stockId: null,
            resourceId: null,
            categoryId: null,
            descricao: '',
            unidadeMedida: '',
            availableVariations: [],
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  const handleMaterialSelect = async (itemId: number, familyMaterialId: number) => {
    const conjunto = familyConjuntos.find(c => c.familyMaterialId === familyMaterialId)
    const matId = conjunto?.materialId || null
    let variations: MaterialVariation[] = []
    if (matId) {
      try {
        const token = localStorage.getItem('token')
        const headers = { Authorization: `Bearer ${token}` }
        const res = await axios.get(`${FASHION_API_URL}/stocks`, {
          headers,
          params: { materialId: matId },
        })
        const stocks = Array.isArray(res.data) ? res.data : []
        const colorsArr: { id: number, name: string, stockId: number }[] = []
        for (const stk of stocks) {
          const cols = await fetchStockColors(stk.id)
          cols.forEach((c: any) => {
            if (!colorsArr.some(x => x.id === c.id)) colorsArr.push({ id: c.id, name: c.color.name, stockId: stk.id })
          })
        }
        variations = colorsArr.map((c: any) => ({ id: c.id, colorId: c.colorId || c.id, stockId: c.stockId, resourceId: null, color: { id: c.colorId || c.id, name: c.name }, stock: null, resource: null }))
      } catch (e) {
        console.error('erro ao buscar variações para material', e)
      }
    }
    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            materialId: matId,
            stockId: null,
            resourceId: null,
            descricao: conjunto?.materialName || item.descricao,
            availableVariations: variations,
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }

  const handleInsumoGroupSelect = async (itemId: number, materialAndCategory: string | null) => {
    if (!materialAndCategory) return
    const [materialRaw, categoryRaw] = String(materialAndCategory).split(':')
    const selectedMaterialId = materialRaw ? Number(materialRaw) : null
    const selectedCategoryId = categoryRaw ? Number(categoryRaw) : null
    const selectedMaterial = insumoMaterialGroups.find(
      g => g.materialId === selectedMaterialId && Number(g.categoryId ?? 0) === Number(selectedCategoryId ?? 0)
    )
    if (!selectedMaterialId) return

    const templateResourceId = getTemplateResourceIdByMaterial(selectedMaterialId, selectedCategoryId)
    if (!templateResourceId) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Não foi possível localizar o recurso correspondente para o insumo selecionado.',
      })
      return;
    }

    const colors = await fetchResourceMaterialColors(selectedMaterialId)
    const variations: MaterialVariation[] = colors.map((c: any) => ({
      id: c.id,
      colorId: c.colorId || c.id,
      stockId: null,
      resourceId: templateResourceId,
      color: { id: c.colorId || c.id, name: c.color?.name || c.name || '' },
      stock: null,
      resource: null,
    }))

    setCompositionItems(prev => {
      const next = prev.map(item =>
        item.id === itemId
          ? {
            ...item,
            materialId: selectedMaterialId,
            stockId: null,
            resourceId: templateResourceId,
            categoryId: selectedCategoryId,
            descricao: selectedMaterial?.materialName || item.descricao,
            availableVariations: variations,

            productItemColors: selectedCollectionColors.map(c => ({ collectionColorId: c.id, itemColorId: null })),
          }
          : item
      );
      const updated = next.find(i => i.id === itemId);
      console.debug('[RC] handleInsumoGroupSelect updated item', updated);
      return next;
    });
  }

  const fetchStockColors = async (stockId?: number, materialId?: number) => {
    console.debug('[RC] fetchStockColors called', { stockId, materialId });
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      if (stockId) {
        const resp = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, { headers })
        const stock = resp.data
        if (stock && stock.StockColors) {
          const result = stock.StockColors.map((sc: any) => ({
            id: sc.id,             // stockColorId
            colorId: sc.colorId,
            stockId,
            color: { id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` },
          }));
          console.debug('[RC] fetchStockColors result count', result.length, 'for stockId', stockId);
          return result;
        }
      } else if (materialId) {
        const resp = await axios.get(`${FASHION_API_URL}/stocks`, { headers, params: { materialId } })
        const stocks: any[] = Array.isArray(resp.data) ? resp.data : []
        const seen: Record<number, boolean> = {}
        const colors: any[] = []
        for (const stk of stocks) {
          if (stk.StockColors) {
            stk.StockColors.forEach((sc: any) => {
              if (!seen[sc.colorId]) {
                seen[sc.colorId] = true
                colors.push({
                  id: sc.id,
                  colorId: sc.colorId,
                  stockId: stk.id,
                  color: { id: sc.colorId, name: sc.ItemColor?.name || `Cor ${sc.colorId}` },
                })
              }
            })
          }
        }
        console.debug('[RC] fetchStockColors result count', colors.length, 'for materialId', materialId);
        return colors
      }
    } catch (err) {
      console.error('Erro ao buscar cores do stock', err)
    }
    return []
  }

  const getTemplateResourceIdByMaterial = (materialId?: number | null, categoryId?: number | null): number | null => {
    if (!materialId) return null

    if (categoryId && resourcesByCategory[categoryId]) {
      const byCategory = resourcesByCategory[categoryId] || []
      const exact = byCategory.find((r) => Number(r.materialId) === Number(materialId))
      if (exact?.id) return exact.id
    }

    const allResources = Object.values(resourcesByCategory).flat()
    const match = allResources.find((r) => Number(r.materialId) === Number(materialId))
    return match?.id || null
  }

  const fetchResourceMaterialColors = async (resourceMaterialId: number) => {
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const resp = await axios.get(`${FASHION_API_URL}/colors`, {
        headers,
        params: { resourceMaterialId },
      })
      const colors = Array.isArray(resp.data) ? resp.data : []
      return colors.map((c: any) => ({
        id: c.id,
        colorId: c.id,
        name: c.name,
        color: { id: c.id, name: c.name || `Cor ${c.id}` },
      }))
    } catch (err) {
      console.error('Erro ao buscar cores por material de recurso', err)
      return []
    }
  }

  const fetchResourceColors = async (resourceId: number) => {
    try {
      const token = localStorage.getItem('token')
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const resp = await axios.get(`${FASHION_API_URL}/resources/${resourceId}/colors`, { headers })
      const colors = Array.isArray(resp.data) ? resp.data : []
      return colors.map((rc: any) => ({ id: rc.itemColorId, color: { id: rc.itemColorId, name: rc.ItemColor?.name || `Cor ${rc.itemColorId}` } }))
    } catch (err) {
      console.error('Erro ao buscar cores do recurso', err)
    }
    return []
  }

  const handleStockChange = async (itemId: number, stockId: number) => {
    let variations: MaterialVariation[] = []
    let stockDescription = ''
    try {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }

      try {
        const stockRes = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, {
          headers,
        })
        if (stockRes.data) {
          stockDescription = stockRes.data.description || stockRes.data.name || `Stock ${stockId}`
        }
      } catch (stockError) {
        console.error('Erro ao carregar descrição do stock:', stockError)
        const stockInList = conjuntoStocks.find(s => s.id === stockId);
        if (stockInList) {
          stockDescription = stockInList.description || `Stock ${stockId}`;
        }
      }

      const colors = await fetchStockColors(stockId)
      variations = colors.map((c: any) => ({
        id: c.id,
        colorId: c.id,
        stockId,
        resourceId: null,
        color: { id: c.id, name: c.color.name },
        stock: null,
        resource: null,
      }))
    } catch (error) {
      console.error('Erro ao carregar variações:', error)
    }


    if (itemId && stockId) {
      try {
        const token = localStorage.getItem('token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const res = await axios.get(`${FASHION_API_URL}/stocks/${stockId}`, { headers })
        if (res.data && res.data.materialId) {
          setCompositionItems(prev =>
            prev.map(it =>
              it.id === itemId
                ? { ...it, materialId: res.data.materialId }
                : it
            )
          )
        }
      } catch (err) {
        console.error('erro ao buscar material a partir do stock (handleStockChange)', err)
      }
    }

    setCompositionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
            ...item,
            stockId,
            descricao: item.tipo === 'materia-prima' ? item.descricao : stockDescription || item.descricao,
            availableVariations: variations,
            productItemColors: item.productItemColors,
          }
          : item
      )
    )
  }


  useEffect(() => {
    compositionItems.forEach(async (item) => {
      if (item.tipo === 'materia-prima' && item.stockId && item.materialId == null) {
        try {
          const token = localStorage.getItem('token')
          const headers = token ? { Authorization: `Bearer ${token}` } : {}
          const res = await axios.get(`${FASHION_API_URL}/stocks/${item.stockId}`, { headers })
          const mat = res.data?.materialId
          if (mat) {
            console.debug('[RC] povoando materialId a partir do stock (effect)', { itemId: item.id, stockId: item.stockId, materialId: mat })
            setCompositionItems(prev =>
              prev.map(it =>
                it.id === item.id ? { ...it, materialId: mat } : it
              )
            )
          }
        } catch (err) {
          console.error('erro ao buscar material a partir do stock (effect)', err)
        }
      }
    })
  }, [compositionItems])

  const handleUnidadeMedidaChange = (itemId: number, unidadeId: number) => {
    const item = compositionItems.find((i) => i.id === itemId)
    if (!item) return

    const selectedUnit = units.find(u => u.id === unidadeId)
    const unidade = selectedUnit?.name || ''
    console.debug('[RC] alteração unidade item', itemId, 'de', item.unitOfMeasurementId, 'para', unidadeId, 'cores anteriores', item.productItemColors)

    setCompositionItems((prev) => {
      const updated = prev.map((i) =>
        i.id === itemId ? { ...i, unidadeMedida: unidade, unitOfMeasurementId: unidadeId } : i
      )
      const updatedItem = updated.find(i => i.id === itemId)
      console.debug('[RC] item após mudança unidade', updatedItem)
      console.debug('[RC] compositionItems after unidade change', updated)
      return updated
    })
  }

  const handleCompositionFieldChange = (
    itemId: number,
    field: keyof CompositionItem,
    value: string
  ) => {
    setCompositionItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    )
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.nomeProduto.trim()) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, preencha o nome do produto.',
      })
      return
    }

    if (!formData.collectionId) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, selecione uma coleção.',
      })
      return
    }

    if (!formData.familyId) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Por favor, selecione uma família.',
      })
      return
    }

    setLoading(true)


    const payload = {
      productCode: formData.codigo,
      name: formData.nomeProduto,
      price: formData.price || undefined,
      collectionId: formData.collectionId,
      familyId: formData.familyId,
      groupId: formData.groupId || undefined,
      gradeIds: selectedGradeIds,
      compositionItems: compositionItems
        .filter((item) => item.tipo === 'materia-prima' || item.tipo === 'insumo')
        .map((item) => {
          const base: any = {
            tipo: item.tipo,

            usage: item.consumo ? Number(String(item.consumo).replace(',', '.')) : undefined,
            unitOfMeasurementId: item.unitOfMeasurementId || undefined,
          }

          if (item.tipo === 'materia-prima') {
            return {
              ...base,
              materialId: item.materialId || undefined,
              stockId: item.stockId || undefined,
            }
          }


          return {
            ...base,
            resourceId: item.resourceId || undefined,
            materialId: item.materialId || undefined,
          }
        }),

      productItemColors: (() => {
        const list = compositionItems.flatMap(item =>
          (item.productItemColors || [])
            .filter(p => p.itemColorId)
            .map(p => {
              const base: any = {
                id: p.id,
                collectionColorId: p.collectionColorId,
                itemColorId: p.itemColorId,
              };
              if (p.stockColorId) base.stockColorId = p.stockColorId;

              if (item.materialId) {
                base.materialId = item.materialId;
              }
              if (p.resourceId) {
                base.resourceId = p.resourceId;
              } else if (item.resourceId) {
                base.resourceId = item.resourceId;
              }
              if (item.stockId) {
                base.stockId = item.stockId;
              }

              return base;
            })
        );


        const byId: Record<string, any> = {};
        list.forEach(entry => {
          if (entry.id !== undefined && entry.id !== null) {
            byId[entry.id] = entry;
          }
        });
        const filtered = list.filter(entry => {
          if (entry.id !== undefined && entry.id !== null) {
            return byId[entry.id] === entry;
          }
          return true;
        });

        const unique = new Map<string, any>();
        filtered.forEach(entry => {
          const key = `${entry.collectionColorId}_${entry.itemColorId}_${entry.resourceId || ''}`;
          if (!unique.has(key)) {
            unique.set(key, entry);
          } else {
            const existing = unique.get(key);

            if (!existing.id && entry.id) unique.set(key, entry);
          }
        });
        return Array.from(unique.values());
      })(),
    }

    try {
      const token = localStorage.getItem('token')


      console.log('[RegisterComposition] payload', JSON.stringify(payload, null, 2));
      console.log('[RegisterComposition] payload compositionItems', payload.compositionItems);
      console.log('[RegisterComposition] compositionItems (raw)', JSON.stringify(compositionItems, null, 2));
      console.log('[RegisterComposition] compositionItems payload mapping', payload.compositionItems.map((i: any) => ({
        tipo: i.tipo,
        materialId: i.materialId,
        resourceId: i.resourceId,
        stockId: i.stockId,
      })));
      console.log('[RegisterComposition] payload colors', payload.productItemColors);
      if (isEditMode && productId) {
        await axios.put(`${FASHION_API_URL}/${productId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Produto atualizado com sucesso!',
        })
      } else {
        await axios.post(`${FASHION_API_URL}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'Produto cadastrado com sucesso!',
        })
      }

      navigate('/panel/product-composition')
    } catch (err: any) {
      console.error('Erro ao salvar produto:', err)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: err.response?.data?.message || 'Erro ao salvar produto. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Composição de produtos', path: '/panel/product-composition' },
    { label: isEditMode ? 'Editar produto' : 'Cadastrar produto' },
  ]

  const hasMaterialItem = React.useMemo(
    () => compositionItems.some((i) => i.tipo === 'materia-prima'),
    [compositionItems],
  )

  const isFormValid = formData.nomeProduto.trim() !== '' && formData.collectionId > 0 && formData.familyId > 0

  const displayGrades = React.useMemo(() => {
    if (!selectedGradeType) return []

    const gradeType = gradeTypes.find(t => t.id === selectedGradeType)
    if (!gradeType) return []

    const gradesOfType = gradesByType[gradeType.name] || []
    return gradesOfType.filter(grade => availableGradeIds.includes(grade.id))
  }, [selectedGradeType, gradeTypes, gradesByType, availableGradeIds])

  if (loadingData || (isEditMode && isInitializing)) {
    return (
      <MainContainer>
        <p>Carregando dados...</p>
      </MainContainer>
    )
  }

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>{isEditMode ? 'Editar composição de produto' : 'Cadastrar composição de produto'}</SectionName>
        </LeftSide>
        <ActionButtons>
          <ImportButton type='button'>
            <DownloadIcon />
            Importar produto base
          </ImportButton>
          <CancelButton type='button' onClick={() => navigate('/panel/product-composition')}>
            Cancelar
          </CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={!isFormValid || loading}>
            <img src={save} alt='Salvar' />
            {loading ? 'Salvando...' : (isEditMode ? 'Atualizar produto' : 'Salvar produto')}
          </SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>

        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={2}>
              <StyledFormGroup>
                <label htmlFor='codigo'>Código do produto</label>
                <input
                  type='text'
                  id='codigo'
                  name='codigo'
                  value={formData.codigo}
                  onChange={handleChange}
                  disabled={isEditMode}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='nomeProduto'>Nome do produto</label>
                <input
                  type='text'
                  id='nomeProduto'
                  name='nomeProduto'
                  value={formData.nomeProduto}
                  onChange={handleChange}
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='price'>Valor (preço do produto)</label>
                <CurrencyInput
                  id='price'
                  name='price'
                  value={formData.price}
                  onValueChange={(_value, _name, values) => {
                    setFormData(prev => ({ ...prev, price: values?.float ?? 0 }))
                  }}
                  placeholder='R$ 0,00'
                />
              </StyledFormGroup>
              <StyledFormGroup>
                <label htmlFor='collectionId'>Coleção vinculada</label>
                <select
                  id='collectionId'
                  name='collectionId'
                  value={formData.collectionId || ''}
                  onChange={handleChange}
                  disabled={isEditMode}
                >
                  <option value=''>Selecione</option>
                  {allCollections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='familyId'>Família</label>
                <select
                  id='familyId'
                  name='familyId'
                  value={formData.familyId || ''}
                  onChange={handleChange}
                  disabled={!formData.collectionId || isEditMode}
                >
                  <option value=''>Selecione</option>
                  {filteredFamilies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </StyledFormGroup>
              <CollectionStyles.StyledFormGroup>
                <label htmlFor='groupId' style={{ minHeight: '16px', display: 'block' }}>Grupo</label>
                <select
                  id='groupId'
                  name='groupId'
                  value={formData.groupId || ''}
                  onChange={(e) => handleChange(e)}
                  disabled={!formData.familyId}
                >
                  <option value=''>Selecione</option>
                  {filteredGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>

              </CollectionStyles.StyledFormGroup>

              <CollectionStyles.StyledFormGroup>
                <label htmlFor='conjunto' style={{ minHeight: '16px', display: 'block' }}>Conjunto (Tecido base)</label>
                <select
                  id='conjunto'
                  value={selectedConjuntoId || ''}
                  onChange={handleConjuntoChange}
                  disabled={!formData.familyId || familyConjuntos.length === 0}
                >
                  <option value=''>Selecione</option>
                  {familyConjuntos.map((c) => (
                    <option key={c.familyMaterialId} value={c.familyMaterialId}>
                      {c.materialName}
                    </option>
                  ))}
                </select>
              </CollectionStyles.StyledFormGroup>


              <StyledFormGroup>
                <label htmlFor='collectionColors'>Cores da coleção/família</label>
                <SelectContainer>
                  <select
                    id='collectionColorsSelect'
                    value=''
                    onChange={(e) => {
                      const id = Number(e.target.value)
                      if (!id) return
                      const col = allCollectionColors.find(c => c.id === id)
                      if (col && !selectedCollectionColors.some(sc => sc.id === col.id)) {
                        setSelectedCollectionColors(prev => [...prev, col])
                      }
                    }}
                    disabled={!formData.familyId || allCollectionColors.length === 0}
                  >
                    <option value=''>Selecione</option>
                    {allCollectionColors.filter(c => !selectedCollectionColors.some(sc => sc.id === c.id)).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </SelectContainer>

                {selectedCollectionColors.length > 0 && (
                  <TagsContainer>
                    {selectedCollectionColors.map((c) => (
                      <Tag key={c.id}>
                        {c.name}
                        <RemoveTagButton onClick={() => removeCollectionColor(c.id)}>
                          <img src={cross} alt='Remover' />
                        </RemoveTagButton>
                      </Tag>
                    ))}
                  </TagsContainer>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label htmlFor='gradeTypes'>Tipo de graduação</label>
                <select
                  value={selectedGradeType || ''}
                  onChange={handleGradeTypeChange}
                  disabled={!formData.familyId || availableGradeIds.length === 0}
                >
                  <option value=''>Selecione</option>
                  {gradeTypes
                    .filter(type => {
                      const gradesOfType = gradesByType[type.name] || []
                      return gradesOfType.some(g => availableGradeIds.includes(g.id))
                    })
                    .map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                </select>
              </StyledFormGroup>
            </FormGrid>


            {displayGrades.length > 0 && (
              <GraduationSection>
                <GraduationLabel>Graduação</GraduationLabel>
                <GraduationGrid>
                  {displayGrades.map((grade) => (
                    <GraduationButton
                      key={grade.id}
                      type='button'
                      $isSelected={selectedGradeIds.includes(grade.id)}
                      onClick={() => handleGradeToggle(grade.id)}
                    >
                      {grade.name}
                    </GraduationButton>
                  ))}
                </GraduationGrid>
              </GraduationSection>
            )}
          </StyledCard>



          <StyledCard id="composition-section">
            <CardTitle>Composição</CardTitle>

            {compositionItems.length > 0 && (
              <>
                <CompositionTableWrapper>
                  <CompositionTable>
                    <CompositionTableHeader>
                      <span>Tipo</span>
                      <span>Material/Insumo</span>
                      <span>Consumo</span>
                      <span>Un. de medida</span>
                      {selectedCollectionColors.map(col => (
                        <span key={`col-header-${col.id}`}>{col.name}</span>
                      ))}
                    </CompositionTableHeader>

                    {compositionItems.map((item, index) => {
                      const canSelectMaterial = !hasMaterialItem || item.tipo === 'materia-prima'

                      return (
                        <CompositionTableRow key={item.id}>
                          <TableCell>
                            <TableSelect
                              value={item.tipo || ''}
                              onChange={(e) => handleCompositionTypeChange(item.id, e.target.value as 'materia-prima' | 'insumo' | '')}
                              disabled={!!item.autoCreated}
                            >
                              <option value=''>Tipo</option>
                              {canSelectMaterial && (
                                <option value='materia-prima'>
                                  Material
                                </option>
                              )}
                              <option value='insumo'>Insumo</option>
                            </TableSelect>
                          </TableCell>

                          <TableCell>
                            {item.tipo === 'materia-prima' ? (
                              <TableSelect
                                value={
                                  (familyConjuntos.find(c => c.materialId === item.materialId)?.familyMaterialId) || ''
                                }
                                onChange={(e) => handleMaterialSelect(item.id, Number(e.target.value))}
                                disabled={!!item.autoCreated}
                              >
                                <option value=''>Selecione material</option>
                                {familyConjuntos.map((c) => (
                                  <option key={c.familyMaterialId} value={c.familyMaterialId}>
                                    {c.materialName}
                                  </option>
                                ))}
                              </TableSelect>
                            ) : (
                              <TableSelect
                                value={item.materialId ? `${item.materialId}:${item.categoryId ?? ''}` : ''}
                                onChange={(e) => handleInsumoGroupSelect(item.id, e.target.value || null)}
                              >
                                <option value=''>Selecione material</option>
                                {(() => {
                                  const selectedMaterialIds = selectedInsumoMaterialIds;

                                  const options = insumoMaterialGroups
                                    .filter(g => {
                                      if (g.materialId == null) return false;

                                      if (item.materialId != null && g.materialId === item.materialId) {
                                        return true;
                                      }
                                      return !selectedMaterialIds.has(g.materialId);
                                    })
                                    .map((g) => ({
                                      key: `${g.materialId ?? 'sem-material'}-${g.categoryId ?? 'sem-categoria'}`,
                                      value: `${g.materialId ?? ''}:${g.categoryId ?? ''}`,
                                      name: g.materialName,
                                    }));

                                  console.log('[RC] insumo options for item', item.id, {
                                    materialId: item.materialId,
                                    selectedMaterialIds: Array.from(selectedMaterialIds).sort(),
                                    available: options.map(o => o.name),
                                    totalOptions: insumoMaterialGroups.length,
                                  });

                                  return options.map((opt) => (
                                    <option key={opt.key} value={opt.value}>
                                      {opt.name}
                                    </option>
                                  ));
                                })()}
                              </TableSelect>
                            )}
                          </TableCell>

                          <TableCell>
                            <TableInput
                              type='text'
                              value={item.consumo}
                              onChange={(e) => {

                                handleCompositionFieldChange(item.id, 'consumo', e.target.value)
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TableSelect
                              value={item.unitOfMeasurementId || ''}
                              onChange={(e) => handleUnidadeMedidaChange(item.id, Number(e.target.value))}
                            >
                              <option value=''></option>
                              {units.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name}
                                </option>
                              ))}
                            </TableSelect>
                          </TableCell>



                          {selectedCollectionColors.map(col => {
                            const existing = item.productItemColors?.find(p => p.collectionColorId === col.id);
                            const isMaterial = item.tipo === 'materia-prima';
                            const params: any = {};
                            if (isMaterial) {

                              const baseMaterial = selectedConjunto?.materialId ?? item.materialId;
                              if (baseMaterial != null) {
                                params.materialId = baseMaterial;
                              } else if (item.stockId != null) {
                                params.stockId = item.stockId;
                              }
                            } else {
                              if (item.materialId != null) {
                                params.resourceMaterialId = item.materialId;
                              }
                            }

                            const hasParams = params && Object.keys(params).length > 0;

                            const templateResourceId = item.materialId ? getTemplateResourceIdByMaterial(item.materialId, item.categoryId) : null;

                            const dynamicEndpoint = {
                              baseUrl: FASHION_API_URL,
                              listPath: '/colors',
                              createPath: '/colors',
                              deletePath: '/colors',
                              searchParam: 'search',
                              additionalParams: hasParams ? params : undefined,
                            };

                            const resolveColorIdFromStock = (scId: number) => {
                              const v = allVariations.find(v => v.id === scId);
                              return v ? v.colorId : null;
                            };
                            let currentColorId: number | null = null;
                            if (existing?.itemColorId) currentColorId = existing.itemColorId;
                            else if (existing?.stockColorId) currentColorId = resolveColorIdFromStock(existing.stockColorId);
                            const getColorName = (id: number) => {
                              const varMatch = allItemColors.find(ai => ai.id === id) ||
                                allCollectionColors.find(ac => ac.id === id);
                              if (varMatch) return varMatch.name;
                              const variation = allVariations?.find(v => v.colorId === id || v.id === id);
                              if (variation) return variation.color?.name || `Cor ${id}`;
                              return `Cor ${id}`;
                            };


                            const handleCreateColor = async (name: string) => {
                              const token = localStorage.getItem('token');
                              const headers = token ? { Authorization: `Bearer ${token}` } : {};
                              const createRes = await axios.post(`${FASHION_API_URL}/colors`, { name }, { headers });
                              const newColor = createRes.data;
                              if (isMaterial && item.materialId) {
                                try {
                                  await axios.post(
                                    `${FASHION_API_URL}/materials/${item.materialId}/stock-colors`,
                                    { colorId: newColor.id },
                                    { headers }
                                  );
                                } catch (err) {
                                  console.error('erro ao associar cor a estoque/material', err);
                                }
                              }
                              if (!isMaterial && item.materialId) {
                                try {
                                  const templateResourceId = getTemplateResourceIdByMaterial(item.materialId, item.categoryId)
                                  if (templateResourceId) {
                                    const resp = await axios.post(
                                      `${FASHION_API_URL}/resources/${templateResourceId}/resource-colors`,
                                      { colorId: newColor.id },
                                      { headers }
                                    );

                                    const newResourceId = resp.data?.resourceId ?? templateResourceId;
                                    setCompositionItems(prev => prev.map(it => {
                                      if (it.id !== item.id) return it;
                                      const updatedProductItemColors = (it.productItemColors || []).map(p =>
                                        p.collectionColorId === col.id ? { ...p, resourceId: newResourceId } : p
                                      );
                                      return { ...it, productItemColors: updatedProductItemColors };
                                    }));
                                  }
                                } catch (err) {
                                  console.error('erro ao associar cor a recurso/material', err);
                                }
                              }

                              return { id: newColor.id, name: newColor.name };
                            };

                            if (!item.materialId && !item.stockId) {
                              return (
                                <TableCell key={`color-cell-${col.id}`}>
                                  <TableSelect disabled value=''>
                                    <option value=''>Selecione material</option>
                                  </TableSelect>
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={`color-cell-${col.id}`}>
                                  <SelectContainer>
                                    <InfiniteSelect
                                      key={`${col.id}-${JSON.stringify(params)}`}
                                      placeholder='Selecione cor do item'
                                      mode='crud'
                                      showSelectedBelow={false}
                                      endpointConfig={dynamicEndpoint}
                                      disabled={!hasParams}
                                      initialOptions={currentColorId ? [{ id: currentColorId, name: getColorName(currentColorId) }] : []}
                                      value={currentColorId ? { id: currentColorId, name: getColorName(currentColorId) } : null}
                                      onItemCreate={item.materialId ? handleCreateColor : undefined}
                                      dropdownForceFixed={true}
                                      dropdownZIndex={2147483600 + index * 10}
                                      onSelect={async (option: any) => {
                                        const token = localStorage.getItem('token');

                                        if (option && option.id != null && option.name) {
                                          setAllItemColors(prev => {
                                            const exists = prev.some(c => String(c.id) === String(option.id));
                                            if (!exists) {
                                              return [...prev, { id: option.id, name: option.name }];
                                            }
                                            return prev;
                                          });
                                        }
                                        const colorVal = option ? Number(option.id) : null;
                                        let stockColorVal: number | undefined;
                                        let entryResourceId: number | undefined;

                                        if (colorVal != null && (item.materialId || item.stockId)) {
                                          try {
                                            const params: any = {};
                                            if (item.materialId) params.materialId = item.materialId;
                                            if (item.stockId) params.stockId = item.stockId;

                                            if (isMaterial) {
                                              const list = await fetchStockColors(item.stockId || undefined, item.materialId || undefined);
                                              const match = list.find((v: any) => v.colorId === colorVal);
                                              if (match) stockColorVal = match.id;
                                            } else if (templateResourceId) {

                                              try {
                                                const resp = await axios.post(
                                                  `${FASHION_API_URL}/resources/${templateResourceId}/resource-colors`,
                                                  { colorId: colorVal },
                                                  { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                                );
                                                entryResourceId = resp.data?.resourceId;
                                              } catch (err) {
                                                console.error('erro ao criar recurso para cor do insumo', err);
                                              }
                                            }
                                          } catch (e) {
                                            console.error('erro ao determinar stockColorId para cor selecionada', e);
                                          }
                                        }

                                        setCompositionItems(prev => prev.map(it => {
                                          if (it.id !== item.id) return it;
                                          const updated: ProductItemColorEntry[] = (it.productItemColors || [])
                                            .filter(p => p.collectionColorId !== col.id);
                                          if (colorVal !== null) {
                                            const entry: ProductItemColorEntry = { collectionColorId: col.id, itemColorId: colorVal };
                                            if (stockColorVal) entry.stockColorId = stockColorVal;
                                            if (entryResourceId) entry.resourceId = entryResourceId;
                                            updated.push(entry);
                                          }
                                          return { ...it, productItemColors: updated };
                                        }));
                                      }}

                                      onItemDelete={async (id: any) => {
                                        setCompositionItems(prev => prev.map(it => {
                                          const updated = (it.productItemColors || []).map(p => ({ ...p }));
                                          for (let i = updated.length - 1; i >= 0; i--) {
                                            if (String(updated[i].itemColorId) === String(id) || String(updated[i].stockColorId) === String(id)) {
                                              updated[i] = { ...updated[i], itemColorId: null, stockColorId: undefined };
                                            }
                                          }
                                          return { ...it, productItemColors: updated };
                                        }));
                                      }}
                                      getOptionLabel={(opt: any) => opt.name
                                        || (allItemColors.find(ai => String(ai.id) === String(opt.id))?.name)
                                        || (allCollectionColors.find(ac => String(ac.id) === String(opt.id))?.name)
                                        || `Cor ${opt.id}`}
                                    />
                                  </SelectContainer>
                                </TableCell>
                              );
                            }
                          })}

                          {!item.autoCreated && (
                            <TableCell>
                              <DeleteButton type='button' onClick={() => removeCompositionItem(item.id)}>
                                <img src={trash} alt='Excluir' />
                              </DeleteButton>
                            </TableCell>
                          )}
                        </CompositionTableRow>
                      );
                    })}
                  </CompositionTable>
                </CompositionTableWrapper>
              </>
            )}

            <AddItemButton type='button' onClick={addCompositionItem}>
              <img src={plus} alt='Adicionar' />
              Adicionar item
            </AddItemButton>
          </StyledCard>
        </form>
      </MainContainer>
    </>
  )
}

export default RegisterComposition